import { onBeforeUnmount, shallowRef } from 'vue'
import type { Ref } from 'vue'
import { shiftByUnits, snapTime } from './timeScale'
import type { DragMode, DragState, FlatRow, TimelineEvent, TimeUnit } from './types'

const MOUSE_THRESHOLD = 4
const TOUCH_SLOP = 8
const EDGE_ZONE = 36
const MAX_SCROLL_SPEED = 18

export interface PointerContext {
  scroller: Ref<HTMLElement | null>
  unit: () => TimeUnit
  snapMinutes: () => number
  cellWidth: () => number
  labelWidth: () => number
  longPressMs: () => number
  timeAtClientX: (clientX: number) => number
  rowAtClientY: (clientY: number) => FlatRow | null
  canDrop: (event: TimelineEvent, row: FlatRow) => boolean
  canCreate: (row: FlatRow) => boolean
  snapRange?: (rowKey: string, start: Date, end: Date, anchor: number) => { start: Date, end: Date } | null
  resizeLimit?: (event: TimelineEvent, edge: 'start' | 'end') => Date | null
  onEventClick: (event: TimelineEvent, rowKey: string, native: PointerEvent) => void
  onCommit: (drag: DragState) => void
}

interface Pending {
  pointerId: number
  pointerType: string
  mode: DragMode
  event: TimelineEvent | null
  rowKey: string
  dragAllowed: boolean
  startX: number
  startY: number
  lastX: number
  lastY: number
  startScrollLeft: number
  anchorTime: number
  active: boolean
  cancelled: boolean
  timer: ReturnType<typeof setTimeout> | null
}

export function usePointerInteractions (ctx: PointerContext) {
  const drag = shallowRef<DragState | null>(null)
  let pending: Pending | null = null
  let scrollFrame: number | null = null

  const pointerX = (p: Pending) => p.lastX + ((ctx.scroller.value?.scrollLeft || 0) - p.startScrollLeft)

  const computeDrag = (p: Pending): DragState => {
    const unit = ctx.unit()
    const snap = ctx.snapMinutes()
    const units = (pointerX(p) - p.startX) / ctx.cellWidth()
    const current = drag.value
    if (p.mode === 'create') {
      const a = p.anchorTime
      const b = ctx.timeAtClientX(p.lastX)
      const start = snapTime(Math.min(a, b), unit, snap, 'floor')
      let end = snapTime(Math.max(a, b), unit, snap, 'ceil')
      if (end <= start) {
        end = unit === 'day' ? shiftByUnits(new Date(start), 'day', 1, snap).getTime() : start + snap * 60000
      }
      const snapped = ctx.snapRange ? ctx.snapRange(p.rowKey, new Date(start), new Date(end), a) : null
      if (snapped) {
        return { mode: 'create', event: null, fromRowKey: p.rowKey, toRowKey: p.rowKey, start: snapped.start, end: snapped.end, changed: true, clientY: p.lastY }
      }
      return { mode: 'create', event: null, fromRowKey: p.rowKey, toRowKey: p.rowKey, start: new Date(start), end: new Date(end), changed: true, clientY: p.lastY }
    }
    const event = p.event as TimelineEvent
    let start = event.start
    let end = event.end
    let toRowKey = current ? current.toRowKey : p.rowKey
    if (p.mode === 'move') {
      start = shiftByUnits(event.start, unit, units, snap)
      end = unit === 'day'
        ? shiftByUnits(event.end, unit, units, snap)
        : new Date(event.end.getTime() + (start.getTime() - event.start.getTime()))
      const row = ctx.rowAtClientY(p.lastY)
      if (row && (row.key === p.rowKey || ctx.canDrop(event, row))) {
        toRowKey = row.key
      }
    } else if (p.mode === 'resize-end') {
      end = shiftByUnits(event.end, unit, units, snap)
      if (unit === 'day') {
        // Day steps keep the time of day, so an overnight stay keeps at least its own length
        while (end <= start) {
          end = shiftByUnits(end, unit, 1, snap)
        }
      } else {
        const min = shiftByUnits(start, unit, snap / 60, snap)
        if (end < min) {
          end = min
        }
      }
      const limit = ctx.resizeLimit ? ctx.resizeLimit(event, 'end') : null
      if (limit && end > limit && limit > start) {
        end = limit
      }
    } else if (p.mode === 'resize-start') {
      start = shiftByUnits(event.start, unit, units, snap)
      if (unit === 'day') {
        while (start >= end) {
          start = shiftByUnits(start, unit, -1, snap)
        }
      } else {
        const max = shiftByUnits(end, unit, -snap / 60, snap)
        if (start > max) {
          start = max
        }
      }
      const limit = ctx.resizeLimit ? ctx.resizeLimit(event, 'start') : null
      if (limit && start < limit && limit < end) {
        start = limit
      }
    }
    const changed = start.getTime() !== event.start.getTime() || end.getTime() !== event.end.getTime() || toRowKey !== p.rowKey
    return { mode: p.mode, event, fromRowKey: p.rowKey, toRowKey, start, end, changed, clientY: p.lastY }
  }

  const stopAutoScroll = () => {
    if (scrollFrame !== null) {
      cancelAnimationFrame(scrollFrame)
      scrollFrame = null
    }
  }

  const autoScroll = () => {
    scrollFrame = null
    const el = ctx.scroller.value
    if (!pending || !pending.active || !el) {
      return
    }
    const rect = el.getBoundingClientRect()
    let dx = 0
    let dy = 0
    const leftEdge = rect.left + ctx.labelWidth()
    if (pending.lastX < leftEdge + EDGE_ZONE) {
      dx = -Math.min(MAX_SCROLL_SPEED, leftEdge + EDGE_ZONE - pending.lastX)
    } else if (pending.lastX > rect.right - EDGE_ZONE) {
      dx = Math.min(MAX_SCROLL_SPEED, pending.lastX - (rect.right - EDGE_ZONE))
    }
    if (pending.lastY < rect.top + EDGE_ZONE + 40) {
      dy = -Math.min(MAX_SCROLL_SPEED, rect.top + EDGE_ZONE + 40 - pending.lastY)
    } else if (pending.lastY > rect.bottom - EDGE_ZONE) {
      dy = Math.min(MAX_SCROLL_SPEED, pending.lastY - (rect.bottom - EDGE_ZONE))
    }
    if (dx || dy) {
      el.scrollLeft += dx
      el.scrollTop += dy
      drag.value = computeDrag(pending)
      scrollFrame = requestAnimationFrame(autoScroll)
    }
  }

  const activate = () => {
    if (!pending || pending.cancelled) {
      return
    }
    pending.active = true
    if (pending.pointerType === 'touch' && typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(10)
    }
    drag.value = computeDrag(pending)
  }

  const cleanup = () => {
    if (pending && pending.timer) {
      clearTimeout(pending.timer)
    }
    pending = null
    stopAutoScroll()
    window.removeEventListener('pointermove', onMove)
    window.removeEventListener('pointerup', onUp)
    window.removeEventListener('pointercancel', onCancel)
    window.removeEventListener('keydown', onKey)
  }

  const onMove = (e: PointerEvent) => {
    if (!pending || e.pointerId !== pending.pointerId) {
      return
    }
    pending.lastX = e.clientX
    pending.lastY = e.clientY
    const distance = Math.hypot(e.clientX - pending.startX, e.clientY - pending.startY)
    if (!pending.active) {
      if (pending.pointerType === 'touch') {
        if (distance > TOUCH_SLOP) {
          pending.cancelled = true
          cleanup()
        }
        return
      }
      if (distance > MOUSE_THRESHOLD) {
        if (pending.dragAllowed) {
          activate()
        } else {
          pending.cancelled = true
          cleanup()
        }
      }
      return
    }
    e.preventDefault()
    drag.value = computeDrag(pending)
    if (scrollFrame === null) {
      scrollFrame = requestAnimationFrame(autoScroll)
    }
  }

  const onUp = (e: PointerEvent) => {
    if (!pending || e.pointerId !== pending.pointerId) {
      return
    }
    const p = pending
    const state = drag.value
    cleanup()
    drag.value = null
    if (p.active) {
      if (state && state.changed) {
        ctx.onCommit(state)
      }
      return
    }
    if (!p.cancelled && p.event) {
      ctx.onEventClick(p.event, p.rowKey, e)
    }
  }

  const onCancel = (e: PointerEvent) => {
    if (!pending || e.pointerId !== pending.pointerId) {
      return
    }
    cleanup()
    drag.value = null
  }

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && pending) {
      cleanup()
      drag.value = null
    }
  }

  const begin = (e: PointerEvent, mode: DragMode, event: TimelineEvent | null, rowKey: string, dragAllowed: boolean) => {
    if (e.pointerType === 'mouse' && e.button !== 0) {
      return
    }
    cleanup()
    pending = {
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      mode,
      event,
      rowKey,
      dragAllowed,
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      startScrollLeft: ctx.scroller.value?.scrollLeft || 0,
      anchorTime: ctx.timeAtClientX(e.clientX),
      active: false,
      cancelled: false,
      timer: null
    }
    if (e.pointerType === 'touch') {
      if (dragAllowed) {
        pending.timer = setTimeout(activate, ctx.longPressMs())
      }
    } else {
      e.preventDefault()
    }
    window.addEventListener('pointermove', onMove, { passive: false })
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onCancel)
    window.addEventListener('keydown', onKey)
  }

  const onEventPointerDown = (e: PointerEvent, event: TimelineEvent, rowKey: string, edge: 'start' | 'end' | null = null) => {
    e.stopPropagation()
    if (event.selectable) {
      begin(e, 'create', event, rowKey, true)
      return
    }
    const canDrag = event.draggable !== false
    let mode: DragMode = 'move'
    if (edge && canDrag && event.resizable !== false) {
      mode = edge === 'start' ? 'resize-start' : 'resize-end'
    }
    begin(e, mode, event, rowKey, canDrag)
  }

  const onTrackPointerDown = (e: PointerEvent, row: FlatRow) => {
    if (!ctx.canCreate(row)) {
      return
    }
    begin(e, 'create', null, row.key, true)
  }

  const isDragging = () => !!(pending && pending.active)

  // Touch scrolling must be blocked while a long-press drag is active
  const onTouchMove = (e: TouchEvent) => {
    if (pending && pending.active) {
      e.preventDefault()
    }
  }

  onBeforeUnmount(cleanup)

  return { drag, onEventPointerDown, onTrackPointerDown, onTouchMove, isDragging }
}
